import { setGuestPassword, ApiError, keepCommunicationPresence, updateCommunication, getHostSessionState, updateHostSession, setPrivateMonitoring, updateParticipant, hostKickParticipant, type HostSessionState, type HostRosterMember } from "./api";

export interface HostAudioControls {
  peers: () => { id: string; label: string; muted: boolean }[];
  setGain: (identity: string, gain: number) => void;
}

/** A separate operator surface. Audio and comments use the app's single room. */
export class HostControls {
  readonly element = document.createElement("main");
  readonly commentsContainer = document.createElement("section");
  readonly chatContainer = document.createElement("aside");
  readonly talkContainer = document.createElement("footer");
  private state: HostSessionState | null = null;
  private disposed = false;
  private busy = false;
  private polling = false;
  private revision = 0;
  private timer: number;
  private peopleKey = "";
  private gains = new Map<string, number>();
  private endArmed = false;
  private endTimer: number | null = null;
  private stopPresence: () => void;
  private events = new AbortController();
  private copyTimer: number | null = null;
  private clockLabel = document.createElement("small");
  private error = document.createElement("p");
  private status = document.createElement("span");
  private musicTitle = document.createElement("h2");
  private musicNote = document.createElement("p");
  private people = document.createElement("div");
  private openButton: HTMLButtonElement;
  private musicButton: HTMLButtonElement;
  private access = document.createElement("select");
  private linkSummary = document.createElement("summary");
  private passwordNote = document.createElement("p");
  private passwordInput = document.createElement("input");
  private accessNote = document.createElement("p");

  constructor(private id: string, private token: string, private self: string,
    private onState: (s: HostSessionState) => void, private audio: HostAudioControls) {
    this.stopPresence = keepCommunicationPresence(id,token);
    this.element.className = "control-room";
    const nav = document.createElement("header"); nav.className = "control-room__nav";
    const brand = document.createElement("span"); brand.className = "control-room__brand"; brand.textContent = "DAW Stream";
    const actions = document.createElement("div");
    const back = document.createElement("a"); back.href = "/host"; back.textContent = "All sessions";
    const copy = this.button("Copy link", async () => {
      const url = new URL(location.href); url.searchParams.delete("host"); await navigator.clipboard.writeText(url.href); copy.textContent = "Link copied";
      if (this.copyTimer !== null) window.clearTimeout(this.copyTimer);
      this.copyTimer = window.setTimeout(() => { copy.textContent = "Copy link"; }, 2500);
    }); actions.append(back);
    const title = document.createElement("div"); title.className = "control-room__identity"; const h1 = document.createElement("h1"); h1.textContent = id;
    this.status.setAttribute("role", "status"); this.status.textContent = "Checking communication…"; title.append(brand, h1); nav.append(title, actions);
    this.openButton = this.button("Open communication", async () => {
      if (!this.state) return;
      if (this.state.communicationOpen && !this.endArmed) {
        this.endArmed = true; this.openButton.textContent = "End call & close chat/comments?";
        this.endTimer = window.setTimeout(() => { this.endArmed = false; this.paint(); }, 5000); return;
      }
      const open = !this.state.communicationOpen; this.endArmed = false;
      // Opening is not a bulk permission reset. Personal exceptions survive.
      await this.change(() => updateCommunication(id, token, open));
    });
    const music = document.createElement("section"); music.className = "control-room__music"; music.setAttribute("aria-label", "Music sharing");
    const musicText = document.createElement("div"); this.musicNote.className = "control-room__muted"; musicText.append(this.musicTitle, this.musicNote);
    const clock = document.createElement("div"); clock.className = "control-room__clock";
    clock.innerHTML = '<div data-clock><span data-clock-sign></span><span data-clock-digits>Not streaming yet</span></div>';
    clock.append(this.clockLabel);
    this.musicButton = this.button("Pause stream", async () => {
      if (this.state) await this.change(() => setPrivateMonitoring(id, token, !(this.state!.privateMonitoring || this.state!.sourcePaused)));
    }); music.append(musicText, clock, this.musicButton);
    this.error.className = "control-room__error"; this.error.setAttribute("role", "alert"); this.error.hidden = true;
    const work = document.createElement("div"); work.className = "control-room__work";
    this.commentsContainer.className = "control-room__comments comments-container";
    const aside = document.createElement("aside"); aside.className = "control-room__people"; aside.setAttribute("aria-label", "Participants");
    this.people.className = "control-room__roster";
    const accessBox = document.createElement("section"); accessBox.className = "control-room__access";
    const accessHeading = document.createElement("h2"); accessHeading.textContent = "Communication";
    accessBox.append(accessHeading, this.status, this.openButton);
    const settings = document.createElement("details"); settings.className = "control-room__settings";
    const summary = this.linkSummary; summary.textContent = "Guest link";
    const label = document.createElement("label"); label.textContent = "New guests"; this.access.setAttribute("aria-label", "New guests");
    for (const [value, text] of [["direct", "Join directly with the link"], ["waiting", "Wait for me to let them in"]] as const) {
      const o = document.createElement("option"); o.value = value; o.textContent = text; this.access.append(o);
    }
    this.access.onchange = () => { const selected = this.access.value; void this.change(async () => {
      if (selected === "direct" && this.state?.waiting) throw new Error("Let waiting guests in first, then change access for new arrivals.");
      await updateHostSession(id, token, { admitOneByOne: selected === "waiting" });
    }); };
    let accessArmedUntil = 0;
    const accessToggle = this.button("", async () => {
      if (this.state?.open && Date.now() > accessArmedUntil) { accessArmedUntil = Date.now() + 5000; accessToggle.textContent = "Confirm disconnect guests"; return; }
      accessArmedUntil = 0;
      await this.change(() => updateHostSession(id, token, { open: !this.state?.open }));
    });
    accessToggle.dataset.listenerAccess = "";
    label.append(this.access); this.accessNote.className = "control-room__muted";
    const linkBody = document.createElement("div"); linkBody.className = "control-room__link-body";
    const passwordLabel = document.createElement("label"); passwordLabel.textContent = "Link password";
    this.passwordInput.type = "password"; this.passwordInput.autocomplete = "new-password"; this.passwordInput.placeholder = "Password";
    passwordLabel.append(this.passwordInput);
    const passwordForm = document.createElement("form");
    const savePassword = document.createElement("button"); savePassword.className = "btn btn--ghost"; savePassword.type = "submit"; savePassword.textContent = "Save password";
    this.passwordInput.required = true;
    passwordForm.onsubmit = e => { e.preventDefault(); void this.change(async () => { await setGuestPassword(id, token, this.passwordInput.value); this.passwordInput.value = ""; }); };
    const removePassword = this.button("Remove password", () => this.change(() => setGuestPassword(id, token, ""))); removePassword.dataset.removePassword = "";
    this.passwordNote.className = "control-room__muted"; this.passwordNote.setAttribute("role", "status");
    passwordForm.append(passwordLabel, savePassword, removePassword, this.passwordNote);
    const accessHelp = document.createElement("p"); accessHelp.className = "control-room__muted"; accessHelp.textContent = "Password changes apply to new joins. Connected guests stay connected.";
    linkBody.append(copy, label, this.accessNote, passwordForm, accessHelp, accessToggle);
    settings.append(summary, linkBody); settings.className = "control-room__link";
    actions.append(settings);
    document.addEventListener("pointerdown", e => { if (!settings.contains(e.target as Node)) settings.open = false; }, { signal: this.events.signal });
    settings.addEventListener("keydown", e => { if (e.key === "Escape") { settings.open = false; summary.focus(); } });
    const guests = document.createElement("details"); guests.className = "control-room__guests"; guests.open = true;
    const guestsSummary = document.createElement("summary"); guestsSummary.textContent = "Guests (0)"; guestsSummary.dataset.guestSummary = "";
    guests.append(guestsSummary, this.people);
    aside.append(accessBox, guests); work.append(aside, this.commentsContainer);
    this.chatContainer.className = "control-room__chat"; this.chatContainer.setAttribute("aria-label", "Session chat");
    this.talkContainer.className = "control-room__talk";
    this.element.append(nav, music, this.error, work, this.chatContainer, this.talkContainer);
    this.paint(); void this.refresh(); this.timer = window.setInterval(() => { if (!this.busy) void this.refresh(); }, 2000);
  }
  private button(text: string, action: () => Promise<void>): HTMLButtonElement {
    const b = document.createElement("button"); b.type = "button"; b.className = "btn btn--ghost"; b.textContent = text;
    b.onclick = () => { void action().catch(e => this.showError(e)); }; return b;
  }
  private showError(e: unknown) {
    if (this.disposed) return;
    this.error.hidden = false;
    this.error.textContent = e instanceof ApiError && (e.status === 401 || e.status === 403)
      ? "Host access expired. Sign in again from All sessions."
      : e instanceof Error ? e.message : "Could not confirm the change. Please retry.";
  }
  private async change(action: () => Promise<unknown>) {
    if (this.busy || this.disposed) return;
    this.revision++; this.busy = true; this.paint(); this.error.hidden = true;
    try { await action(); await this.refresh(true); } catch (e) { this.showError(e); }
    finally { this.busy = false; this.peopleKey = ""; this.paint(true); }
  }
  private async refresh(afterChange = false) {
    if (this.disposed || (this.polling && !afterChange)) return;
    const revision = this.revision; if (!afterChange) this.polling = true;
    try {
      const state = await getHostSessionState(this.id, this.token);
      if (this.disposed || revision !== this.revision) return;
      this.state = state; this.onState(state); this.paint();
    } catch (e) { this.showError(e); }
    finally { if (!afterChange) this.polling = false; }
  }
  refreshAudio(): void { this.paint(); }
  private paint(force = false) {
    if (this.disposed) return;
    const s = this.state;
    // Do not disable independent comment/audio controls while a host command runs.
    for (const b of [this.openButton, this.musicButton, this.access]) b.disabled = this.busy || !s;
    this.people.querySelectorAll<HTMLButtonElement>("button").forEach(b => b.disabled = this.busy);
    this.people.querySelectorAll<HTMLInputElement>("input[data-permission]").forEach(input => {
      const member = s?.members.find(m => m.clientId === input.dataset.clientId);
      const permission = input.dataset.permission as "canCall" | "canChat" | "canComment" | "canPause";
      input.disabled = this.busy || !member || (permission === "canCall" && !s?.communicationOpen);
      if (member) input.checked = member[permission] ?? (permission === "canComment" || permission === "canChat");
    });
    if (!s) return;
    this.status.textContent = s.communicationOpen ? "Chat and comments open" : "Listening only";
    if (!this.endArmed) this.openButton.textContent = s.communicationOpen ? "Close communication" : "Open communication";
    const paused = s.privateMonitoring || s.sourcePaused;
    this.element.classList.toggle("control-room--private", paused);
    this.musicTitle.textContent = !s.sourceConnected ? "DAW disconnected" : paused ? "Stream paused" : "Stream connected";
    this.musicNote.textContent = !s.sourceConnected ? "Connect the DAW Stream plugin." : paused ? (s.communicationOpen ? "Listen privately in the DAW. The call stays connected." : "Listen privately in the DAW.") : "";
    this.musicNote.hidden = !this.musicNote.textContent;
    this.clockLabel.textContent = paused ? "Paused at" : "DAW position";
    this.musicButton.textContent = paused ? "Resume stream" : "Pause stream";
    this.musicButton.setAttribute("aria-pressed", String(paused));
    const accessToggle = this.element.querySelector<HTMLButtonElement>("[data-listener-access]");
    if (accessToggle && document.activeElement !== accessToggle) accessToggle.textContent = s.open ? "Disconnect guests & close access" : "Reopen guest access";
    this.linkSummary.textContent = !s.open ? "Guest link · Closed" : s.passwordProtected ? "Guest link · Password protected" : "Guest link";
    this.passwordNote.textContent = s.passwordProtected ? "Password protection is on." : "No password set.";
    const removePassword = this.element.querySelector<HTMLButtonElement>("[data-remove-password]");
    if (removePassword) { removePassword.hidden = !s.passwordProtected; removePassword.disabled = this.busy; }
    this.element.querySelectorAll<HTMLButtonElement>(".control-room__link button").forEach(b => b.disabled = this.busy || !s);
    if (force || (!this.busy && document.activeElement !== this.access)) this.access.value = s.admitOneByOne ? "waiting" : "direct";
    this.accessNote.textContent = s.waiting ? "Let waiting guests in before switching to direct access." : "";
    this.accessNote.hidden = !this.accessNote.textContent;
    const members = s.members.filter(m => m.clientId !== this.self && m.present && !m.kicked);
    const guestsSummary = this.element.querySelector<HTMLElement>("[data-guest-summary]");
    if (guestsSummary) guestsSummary.textContent = `Guests (${members.length})${s.waiting ? ` · ${s.waiting} waiting` : ""}`;
    const peers = this.audio.peers();
    for (const m of members) if (m.identity && this.gains.has(m.clientId)) this.audio.setGain(m.identity, this.gains.get(m.clientId)!);
    const key = JSON.stringify([s.open, s.communicationOpen, members, peers]);
    // Preserve an open menu or a focused gain slider across background polls.
    if (key === this.peopleKey || this.busy || (!force && (this.people.contains(document.activeElement) || this.people.querySelector("details[open]")))) return;
    this.peopleKey = key; this.people.replaceChildren();
    for (const [waiting, title] of [[true, "Waiting room"], [false, "Guests"]] as const) {
      const group = members.filter(m => !!m.waiting === waiting); if (!group.length && waiting) continue;
      if (waiting) { const h = document.createElement("h2"); h.textContent = `${title} (${group.length})`; this.people.append(h); }
      for (const m of group) this.people.append(this.person(m));
    }
    if (!members.length) { const p = document.createElement("p"); p.className = "control-room__muted"; p.textContent = "No guests connected."; this.people.append(p); }
  }
  private person(m: HostRosterMember) {
    const row = document.createElement("div"); row.className = "control-room__person";
    const name = document.createElement("div"); name.className = "control-room__name"; name.textContent = m.name;
    const state = document.createElement("small");
    const peer = this.audio.peers().find(p => p.id === m.identity);
    state.textContent = m.waiting ? "Waiting to join" : !m.canCall ? "Listener" : peer && !peer.muted ? "Microphone on" : "Microphone off";
    name.append(state); row.append(name);
    if (m.waiting) row.append(this.button(this.state?.open ? "Let in" : "Open access & let in", () => this.change(async () => {
      if (!this.state?.open) await updateHostSession(this.id, this.token, { open: true, admitOneByOne: true });
      await updateParticipant(this.id, this.token, m.clientId, { admit: true });
    })));
    if (!m.waiting) {
      const permissions = document.createElement("div"); permissions.className = "control-room__permissions";
      permissions.setAttribute("role", "group"); permissions.setAttribute("aria-label", `Permissions for ${m.name}`);
      for (const [key, text, hint] of [
        ["canCall", "Call", "Allow microphone use while communication is open. The guest switches their own microphone on."],
        ["canChat", "Chat", "Allow chat messages while communication is open."],
        ["canComment", "Comments", "Allow time-linked comments while communication is open."],
        ["canPause", "DAW pause", "Allow this guest to pause DAW playback."],
      ] as const) {
        const label = document.createElement("label"); label.title = hint;
        const input = document.createElement("input"); input.type = "checkbox"; input.checked = m[key] ?? (key === "canComment" || key === "canChat");
        input.dataset.permission = key; input.dataset.clientId = m.clientId;
        input.setAttribute("aria-label", `${text} permission for ${m.name}`);
        input.disabled = this.busy || (key === "canCall" && !this.state?.communicationOpen);
        input.onchange = () => { const checked = input.checked; void this.change(() => updateParticipant(this.id, this.token, m.clientId, { [key]: checked })); };
        label.append(input, document.createTextNode(text)); permissions.append(label);
      }
      row.append(permissions);
    }
    let armedUntil = 0;
    const remove = this.button("Remove", async () => {
      if (Date.now() > armedUntil) { armedUntil = Date.now() + 5000; remove.textContent = "Remove?"; return; }
      await this.change(() => hostKickParticipant(this.id, { clientId: m.clientId }, this.token));
    }); remove.title = "Remove from session (click again to confirm)"; remove.classList.add("control-room__remove"); row.append(remove);
    if (!m.waiting && m.canCall) {
      const label = document.createElement("label"); label.className = "control-room__gain"; label.textContent = "Your level";
      const slider = document.createElement("input"); slider.type = "range"; slider.min = "0"; slider.max = "200"; slider.step = "1";
      slider.value = String(Math.round((this.gains.get(m.clientId) ?? 1) * 100)); slider.setAttribute("aria-label", `Your listening level for ${m.name}`);
      const output = document.createElement("span"); const format = () => output.textContent = Number(slider.value) === 0 ? "Muted" : `${Math.round(20 * Math.log10(Number(slider.value) / 100))} dB`;
      format(); slider.oninput = () => { const gain = Number(slider.value) / 100; this.gains.set(m.clientId, gain); if (m.identity) this.audio.setGain(m.identity, gain); format(); };
      slider.ondblclick = () => { slider.value = "100"; slider.dispatchEvent(new Event("input")); };
      label.title = "Only changes what you hear. Double-click to reset to 0 dB."; label.append(slider, output); row.append(label);
    }
    return row;
  }
  destroy() { this.events.abort(); this.disposed = true; this.stopPresence(); window.clearInterval(this.timer); if (this.endTimer !== null) window.clearTimeout(this.endTimer); if (this.copyTimer !== null) window.clearTimeout(this.copyTimer); }
}
